/**
 * Client-side Firebase configuration.
 * All values come from VITE_ environment variables — never hardcode credentials here.
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, Firestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
};

// Prevent double-initialisation in HMR
const app: FirebaseApp = getApps().length
  ? getApps()[0]
  : initializeApp(firebaseConfig);

export const auth: Auth               = getAuth(app);
export const db: Firestore            = getFirestore(app);
// Round 57, A3/B — the problem reporter's screenshot/attachment uploads are
// the first client-side Storage writes in this app (lesson media is
// Admin-SDK-uploaded, server-side only). Same getX(app) + emulator-connect
// shape as auth/db above.
export const storage: FirebaseStorage  = getStorage(app);

// EMULATOR MODE — only active when VITE_USE_EMULATOR=true (set by
// frontend/.env.emulator, loaded via `vite --mode emulator`). Default `npm
// run dev` never sets this, so live behavior is unchanged. Guarded against
// HMR re-running this module and trying to connect twice.
const g = globalThis as unknown as { __emiEmulatorConnected?: boolean };
if (import.meta.env.VITE_USE_EMULATOR === 'true' && import.meta.env.DEV && !g.__emiEmulatorConnected) {
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectStorageEmulator(storage, 'localhost', 9199);
  g.__emiEmulatorConnected = true;
  // eslint-disable-next-line no-console
  console.log('%c[EMI] EMULATOR MODE — connected to local Firestore (:8080) + Auth (:9099), NOT live.', 'color: orange; font-weight: bold;');
}

export default app;
